// src\themes\tolica\components\BannerSlider.tsx

"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { SliderItemFrom } from "@/core/models/slider-model";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { Heading } from "@/components/ui/Heading";
import { cn } from "@/core/lib/utils";
import Link from "next/link";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")

type SliderItemMediaProps = {
  url: string;
  alt?: string;
  className?: string;
  onEnded?: () => void;
  videoRef?: (el: HTMLVideoElement | null) => void; // NEW
};

const isVideoUrl = (url?: string) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

function SliderMedia({ url, alt, className, onEnded, videoRef }: SliderItemMediaProps) {
  if (isVideoUrl(url)) {
    return (
      <video
        ref={videoRef}
        src={url}
        autoPlay
        muted
        playsInline
        onEnded={onEnded}
        className={cn(
          "absolute inset-0 w-full h-full object-cover z-0",
          className
        )}
      />
    );
  }

  return <Image src={url} alt={alt || ""} fill className={className} />;
}

type BannerSliderProps = {
  slides: SliderItemFrom[];
  varient?: "full" | "short";
  onlyDesktop?: boolean;
};

export default function BannerSlider({
  slides,
  varient = "full",
  onlyDesktop = false,
}: BannerSliderProps) {
  if (!Array.isArray(slides)) {
    console.warn("BannerSlider: slides prop must be an array", slides);
    return null; // or return a fallback UI
  }
  const swiperRef = useRef<SwiperClass | null>(null);
  const advancingRef = useRef(false);
  // NEW: نگه‌داشتن رفرنس تمام ویدئوهای فعال (ممکنه چند نسخه دسکتاپ/موبایل یا کلون loop باشه)
  const videoRefs = useRef<Map<number, Set<HTMLVideoElement>>>(new Map());

  const registerVideoRef = (index: number) => (el: HTMLVideoElement | null) => {
    const map = videoRefs.current;
    let set = map.get(index);
    if (!set) {
      set = new Set();
      map.set(index, set);
    }
    if (el) {
      set.add(el);
    }
    // توجه: عمداً عناصر unmount شده رو حذف نمی‌کنیم چون React ref با unmount خودش null صدا می‌زنه،
    // ولی چون Swiper اسلایدها رو دوباره mount نمی‌کنه (فقط جابجا میشه)، این حالت به‌ندرت پیش میاد.
  };

  const isSlideVideoAt = (index: number) => {
    const slide = slides[index];
    return (
      isVideoUrl(slide?.image_info?.url) ||
      isVideoUrl(slide?.image_mobile_info?.url)
    );
  };

  const playVideosForSlide = (index: number) => {
    const set = videoRefs.current.get(index);
    if (!set) return;
    set.forEach((video) => {
      try {
        video.currentTime = 0;
        video.muted = true; // اطمینان از autoplay policy مرورگرها
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            // اگر autoplay بلاک شد (مثلاً به خاطر تعامل نشدن کاربر)، بی‌صدا نادیده بگیر
          });
        }
      } catch {
        // ignore
      }
    });
  };

  const syncAutoplayWithActiveSlide = (swiper: SwiperClass) => {
    advancingRef.current = false;
    const activeIndex = swiper.realIndex;
    if (isSlideVideoAt(activeIndex)) {
      swiper.autoplay.stop();
      playVideosForSlide(activeIndex); // NEW: هربار که به این اسلاید می‌رسیم، از اول پخش کن
    } else {
      swiper.autoplay.start();
    }
  };

  const handleVideoEnded = () => {
    const swiper = swiperRef.current;
    if (!swiper || advancingRef.current) return;
    advancingRef.current = true;
    swiper.slideNext();
  };

  return (
    <Swiper
      modules={[Navigation, Autoplay]}
      spaceBetween={30}
      slidesPerView={1}
      navigation={{
        nextEl: ".swiper-button-next-custom",
        prevEl: ".swiper-button-prev-custom",
      }}
      loop
      autoplay={{
        delay: 3000,
        disableOnInteraction: false,
      }}
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
        syncAutoplayWithActiveSlide(swiper);
      }}
      onSlideChange={syncAutoplayWithActiveSlide}
      className="w-full"
    >
      {slides?.map((slide, index) => {
        const ImageElement = (() => {
          if (onlyDesktop) {
            return (
              slide.image_info?.url && (
                <SliderMedia
                  url={slide.image_info.url}
                  alt={slide.image_info.description || ""}
                  className="w-full h-svh object-cover z-0 absolute"
                  onEnded={handleVideoEnded}
                  videoRef={registerVideoRef(index)} // NEW
                />
              )
            );
          }

          return (
            <>
              <div className="hidden lg:flex">
                {slide.image_info?.url && (
                  <SliderMedia
                    url={slide.image_info.url}
                    alt={slide.image_info.description || ""}
                    className="w-full h-svh object-cover z-0 absolute"
                    onEnded={handleVideoEnded}
                    videoRef={registerVideoRef(index)} // NEW
                  />
                )}
              </div>
              <div className="flex lg:hidden">
                {slide.image_mobile_info?.url && (
                  <SliderMedia
                    url={slide.image_mobile_info.url}
                    alt={slide.image_mobile_info.description || ""}
                    className="w-full h-svh object-cover z-0 absolute"
                    onEnded={handleVideoEnded}
                    videoRef={registerVideoRef(index)} // NEW
                  />
                )}
              </div>
            </>
          );
        })();

        const Content = (
          <div
            className={cn(
              "relative flex justify-center items-center",
              varient === "full" ? "h-[calc(100vh-4.5rem)]" : "h-150"
            )}
          >
            {ImageElement}

            <div className="absolute inset-0 bg-black/20 z-0" />
            <div className="text-primary-100 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 z-10 space-y-3">
              <Heading
                level={1}
                className="text-primary-0 text-[calc(1.2rem+0.3vw)]"
              >
                {slide.title}
              </Heading>
              <Heading level={2} className="text-primary-0">
                {slide.description}
              </Heading>
            </div>
          </div>
        );

        return (
          <SwiperSlide key={slide.priority}>
            {slide.link ? <Link href={slide.link}>{Content}</Link> : Content}
          </SwiperSlide>
        );
      })}

      <button className="swiper-button-prev-custom absolute ltr:left-4 top-1/2 -translate-y-1/2 z-10 text-primary-100 cursor-pointer disabled:opacity-0 disabled:cursor-default rtl:rotate-180 rtl:right-4">
        <ChevronLeft className="w-10 h-10 stroke-1 text-primary-0" />
      </button>
      <button className="swiper-button-next-custom absolute ltr:right-4 top-1/2 -translate-y-1/2 z-10 text-primary-100 cursor-pointer disabled:opacity-0 disabled:cursor-default rtl:rotate-180 rtl:left-4">
        <ChevronRight className="w-10 h-10 stroke-1 text-primary-0" />
      </button>
    </Swiper>
  );
}